import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Dialog as MuiDialog, DialogTitle } from "@mui/material";

const clamp = (value, min, max) => Math.min(Math.max(min, value), max);

const isDialogTitle = (child) => (
  React.isValidElement(child) &&
  (child.type === DialogTitle || child.type?.muiName === "MuiDialogTitle")
);

export default function ConsoleDialog({
  PaperProps = {},
  children,
  movable = true,
  resizable = true,
  ...props
}) {
  const [pos, setPos] = useState(null);
  const [size, setSize] = useState(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  const clampPos = useCallback((x, y, width = 720, height = 420) => {
    const margin = 8;
    const minVisibleX = Math.min(180, Math.max(90, width / 3));
    const minVisibleY = 80;
    return {
      x: clamp(x, margin, Math.max(margin, window.innerWidth - minVisibleX)),
      y: clamp(y, margin, Math.max(margin, window.innerHeight - minVisibleY)),
    };
  }, []);

  const clampSize = useCallback((width, height) => {
    const margin = 8;
    return {
      width: clamp(width, 360, Math.max(360, window.innerWidth - margin * 2)),
      height: clamp(height, 220, Math.max(220, window.innerHeight - margin * 2)),
    };
  }, []);

  const stopDrag = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.move) window.removeEventListener("pointermove", drag.move);
    if (drag?.stop) {
      window.removeEventListener("pointerup", drag.stop);
      window.removeEventListener("pointercancel", drag.stop);
    }
  }, []);

  const stopResize = useCallback(() => {
    const resize = resizeRef.current;
    resizeRef.current = null;
    if (resize?.move) window.removeEventListener("pointermove", resize.move);
    if (resize?.stop) {
      window.removeEventListener("pointerup", resize.stop);
      window.removeEventListener("pointercancel", resize.stop);
    }
  }, []);

  const startDrag = useCallback((event) => {
    if (!movable || event.defaultPrevented || event.button !== 0) return;
    if (event.target.closest("button,a,input,textarea,select,[role='button']")) return;
    const paper = event.currentTarget.closest(".MuiPaper-root");
    if (!paper) return;
    const rect = paper.getBoundingClientRect();
    const drag = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      move: (moveEvent) => {
        const current = dragRef.current;
        if (!current) return;
        setPos(clampPos(
          moveEvent.clientX - current.offsetX,
          moveEvent.clientY - current.offsetY,
          current.width,
          current.height
        ));
      },
      stop: stopDrag,
    };
    dragRef.current = drag;
    setPos(clampPos(rect.left, rect.top, rect.width, rect.height));
    window.addEventListener("pointermove", drag.move);
    window.addEventListener("pointerup", drag.stop);
    window.addEventListener("pointercancel", drag.stop);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* best effort */ }
    event.preventDefault();
  }, [clampPos, movable, stopDrag]);

  const startResize = useCallback((event) => {
    if (!resizable || event.button !== 0) return;
    const paper = event.currentTarget.closest(".MuiPaper-root");
    if (!paper) return;
    const rect = paper.getBoundingClientRect();
    const resize = {
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      move: (moveEvent) => {
        const current = resizeRef.current;
        if (!current) return;
        setSize(clampSize(
          current.width + moveEvent.clientX - current.startX,
          current.height + moveEvent.clientY - current.startY
        ));
      },
      stop: stopResize,
    };
    resizeRef.current = resize;
    setSize(clampSize(rect.width, rect.height));
    window.addEventListener("pointermove", resize.move);
    window.addEventListener("pointerup", resize.stop);
    window.addEventListener("pointercancel", resize.stop);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* best effort */ }
    event.preventDefault();
    event.stopPropagation();
  }, [clampSize, resizable, stopResize]);

  useEffect(() => () => {
    stopDrag();
    stopResize();
  }, [stopDrag, stopResize]);

  const enhancedChildren = React.Children.map(children, (child) => {
    if (!isDialogTitle(child)) return child;
    const onPointerDown = (event) => {
      child.props.onPointerDown?.(event);
      startDrag(event);
    };
    const onDoubleClick = (event) => {
      child.props.onDoubleClick?.(event);
      if (!event.defaultPrevented) setPos(null);
    };
    return React.cloneElement(child, {
      onPointerDown,
      onDoubleClick,
      title: child.props.title || "Hold and drag to move. Double-click to center.",
      sx: [
        { cursor: "grab", userSelect: "none", touchAction: "none" },
        child.props.sx,
      ],
    });
  });

  return (
    <MuiDialog
      {...props}
      PaperProps={{
        ...PaperProps,
        sx: [
          {
            ...(pos ? { position: "fixed", top: pos.y, left: pos.x, m: 0 } : { position: "relative" }),
            ...(size ? {
              width: size.width,
              maxWidth: "calc(100vw - 16px)",
              height: size.height,
              maxHeight: "calc(100vh - 16px)",
            } : {}),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          PaperProps.sx,
        ],
      }}
    >
      {enhancedChildren}
      {resizable ? (
        <Box
          onPointerDown={startResize}
          title="Hold and drag corner to resize."
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 32,
            height: 32,
            cursor: "nwse-resize",
            zIndex: 20,
            borderBottomRightRadius: "inherit",
            background: "linear-gradient(135deg, transparent 0 50%, rgba(25,118,210,0.16) 50% 100%)",
            "&::before": {
              content: '""',
              position: "absolute",
              right: 7,
              bottom: 7,
              width: 14,
              height: 14,
              borderRight: "3px solid rgba(25,118,210,0.85)",
              borderBottom: "3px solid rgba(25,118,210,0.85)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              right: 14,
              bottom: 14,
              width: 7,
              height: 7,
              borderRight: "2px solid rgba(25,118,210,0.65)",
              borderBottom: "2px solid rgba(25,118,210,0.65)",
            },
          }}
        />
      ) : null}
    </MuiDialog>
  );
}
